import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { Product, ConsultationItem, Consultation, CustomerData, ConsultationContextType } from '../types';
import { useAuth } from './AuthContext';
import { consultationService } from '../services/consultationService';
import { productService } from '../services/productService';
import { toast } from 'react-toastify';

type ConsultationAction =
  | { type: 'ADD_TO_CONSULTATION'; payload: { product: Product; quantity: number; notes?: string } }
  | { type: 'REMOVE_FROM_CONSULTATION'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { productId: string; quantity: number } }
  | { type: 'UPDATE_NOTES'; payload: { productId: string; notes: string } }
  | { type: 'UPDATE_CUSTOMER_DATA'; payload: CustomerData }
  | { type: 'CLEAR_CONSULTATION' }
  | { type: 'LOAD_CONSULTATION'; payload: ConsultationItem[] };

const ConsultationContext = createContext<ConsultationContextType | undefined>(undefined);

const consultationReducer = (state: Consultation, action: ConsultationAction): Consultation => {
  switch (action.type) {
    case 'ADD_TO_CONSULTATION': {
      const existingItem = state.items.find(
        item => item.product.id === action.payload.product.id
      );
      
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.product.id === action.payload.product.id
              ? { 
                  ...item, 
                  quantity: item.quantity + action.payload.quantity,
                  notes: action.payload.notes || item.notes
                }
              : item
          ),
          totalItems: state.totalItems + action.payload.quantity
        };
      }
      
      return {
        ...state,
        items: [...state.items, {
          product: action.payload.product,
          quantity: action.payload.quantity,
          notes: action.payload.notes
        }],
        totalItems: state.totalItems + action.payload.quantity
      };
    }
    
    case 'REMOVE_FROM_CONSULTATION': {
      const itemToRemove = state.items.find(item => item.product.id === action.payload);
      return {
        ...state,
        items: state.items.filter(item => item.product.id !== action.payload),
        totalItems: state.totalItems - (itemToRemove?.quantity || 0)
      };
    }
    
    case 'UPDATE_QUANTITY': {
      const oldItem = state.items.find(item => item.product.id === action.payload.productId);
      const oldQuantity = oldItem?.quantity || 0;
      
      return {
        ...state,
        items: state.items.map(item =>
          item.product.id === action.payload.productId
            ? { ...item, quantity: action.payload.quantity }
            : item
        ).filter(item => item.quantity > 0),
        totalItems: state.totalItems - oldQuantity + action.payload.quantity
      };
    }
    
    case 'UPDATE_NOTES':
      return {
        ...state,
        items: state.items.map(item =>
          item.product.id === action.payload.productId
            ? { ...item, notes: action.payload.notes }
            : item
        )
      };
    
    case 'UPDATE_CUSTOMER_DATA':
      return {
        ...state,
        customerData: action.payload
      };
    
    case 'CLEAR_CONSULTATION':
      return { 
        items: [], 
        totalItems: 0,
        customerData: {
          name: '',
          email: '',
          phone: '',
          message: ''
        }
      };
    
    case 'LOAD_CONSULTATION':
      const totalItems = action.payload.reduce((total, item) => total + item.quantity, 0);
      return { 
        ...state,
        items: action.payload, 
        totalItems 
      };
    
    default:
      return state;
  }
};

export const ConsultationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [consultation, dispatch] = useReducer(consultationReducer, { 
    
    items: [], 
    totalItems: 0,
    customerData: {
      name: '',
      email: '',
      phone: '',
      message: ''
    }
  });
  const saveConsultation = async () => {
    if (!user) {
      toast.error('Debes estar logueado para hacer una consulta');
      return false;
    }

    if (consultation.items.length === 0) {
      toast.error('Agrega al menos un producto a la consulta');
      return false;
    }

    try {
      const consultationData = {
        customerData: { // Cambiar de customer_data a customerData
          name: user.name,
          email: user.email,
          phone: user.phone || consultation.customerData.phone
        },
        items: consultation.items // Usar directamente consultation.items
      };

      await consultationService.createConsultation(consultationData);
      toast.success('Consulta enviada exitosamente');
      clearConsultation();
      return true;
    } catch (error) {
      console.error('Error saving consultation:', error);
      toast.error('Error al enviar la consulta');
      return false;
    }
  };

  useEffect(() => {
    const savedConsultation = localStorage.getItem('consultation');
    if (savedConsultation) {
      try {
        const consultationItems: ConsultationItem[] = JSON.parse(savedConsultation);
        dispatch({ type: 'LOAD_CONSULTATION', payload: consultationItems });

        // Revalidar en segundo plano para actualizar precios/ofertas/stock en tiempo real
        if (Array.isArray(consultationItems) && consultationItems.length > 0) {
          Promise.all(
            consultationItems.map(async (item) => {
              try {
                if (item?.product?.id) {
                  const latest = await productService.getProduct(String(item.product.id));
                  if (latest && latest.id) {
                    return {
                      ...item,
                      product: {
                        ...item.product,
                        ...latest,
                        price: latest.price !== undefined ? latest.price : item.product.price,
                        offer_price: latest.offer_price !== undefined ? latest.offer_price : item.product.offer_price,
                        use_offer_price: latest.use_offer_price !== undefined ? latest.use_offer_price : item.product.use_offer_price,
                        stock: latest.stock !== undefined ? latest.stock : item.product.stock,
                      }
                    };
                  }
                }
              } catch (e) {
                // Fallback: conservar datos locales si la llamada falla
              }
              return item;
            })
          ).then((updatedItems) => {
            dispatch({ type: 'LOAD_CONSULTATION', payload: updatedItems });
          });
        }
      } catch (error) {
        localStorage.removeItem('consultation');
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('consultation', JSON.stringify(consultation.items));
  }, [consultation.items]);

  const addToConsultation = (product: Product, quantity: number = 1, notes?: string) => {
    dispatch({ type: 'ADD_TO_CONSULTATION', payload: { product, quantity, notes } });
  };

  const removeFromConsultation = (productId: string) => {
    dispatch({ type: 'REMOVE_FROM_CONSULTATION', payload: productId });
  };

  const updateQuantity = (productId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { productId, quantity } });
  };

  const updateNotes = (productId: string, notes: string) => {
    dispatch({ type: 'UPDATE_NOTES', payload: { productId, notes } });
  };

  const updateCustomerData = (data: CustomerData) => {
    dispatch({ type: 'UPDATE_CUSTOMER_DATA', payload: data });
  };

  const clearConsultation = () => {
    dispatch({ type: 'CLEAR_CONSULTATION' });
  };

  const generateWhatsAppMessage = (): string => {
    // Debug: verificar qué datos tenemos
    console.log('=== DEBUG WHATSAPP MESSAGE ===');
    console.log('user object:', user);
    console.log('user authenticated:', !!user);
    console.log('user name:', user?.name);
    console.log('user email:', user?.email);
    console.log('user dni:', user?.dni);
    console.log('consultation.customerData:', consultation.customerData);
    
    // Usar datos del usuario autenticado como fallback - CORREGIDO
    const name = consultation.customerData.name || user?.name || 'Cliente sin nombre';
    const email = consultation.customerData.email || user?.email || 'sin-email@ejemplo.com';
    const dni = user?.dni || 'No especificado';
    const { message } = consultation.customerData;
    
    console.log('Final name:', name);
    console.log('Final email:', email);
    console.log('Final dni:', dni);
    
    let whatsappMessage = `¡Hola! Me interesa consultar sobre los siguientes productos:\n\n`;
    
    consultation.items.forEach((item, index) => {
      whatsappMessage += `${index + 1}. *${item.product.name}*\n`;
      whatsappMessage += `   Cantidad: ${item.quantity}\n`;
      if (item.product.brand) {
        whatsappMessage += `   Marca: ${item.product.brand}\n`;
      }
      if (item.product.model) {
        whatsappMessage += `   Modelo: ${item.product.model}\n`;
      }
      if (item.notes) {
        whatsappMessage += `   Notas: ${item.notes}\n`;
      }
      whatsappMessage += `\n`;
    });
    
    whatsappMessage += `*Datos de contacto:*\n`;
    whatsappMessage += `Nombre: ${name}\n`;
    whatsappMessage += `Email: ${email}\n`;
    whatsappMessage += `DNI: ${dni}\n`;
    
    if (message) {
      whatsappMessage += `\nMensaje adicional: ${message}\n`;
    }
    
    whatsappMessage += `\n¡Espero su respuesta con los precios y disponibilidad!`;
    
    console.log('Final message:', whatsappMessage);
    console.log('=== END DEBUG ===');
    
    return whatsappMessage;
  };

  const value: ConsultationContextType = {
    consultation,
    addToConsultation,
    removeFromConsultation,
    updateQuantity,
    updateNotes,
    clearConsultation,
    updateCustomerData,
    generateWhatsAppMessage,
    saveConsultation,
    itemCount: consultation.totalItems
  };

  return (
    <ConsultationContext.Provider value={value}>
      {children}
    </ConsultationContext.Provider>
  );
};

export const useConsultation = () => {
  const context = useContext(ConsultationContext);
  if (context === undefined) {
    throw new Error('useConsultation must be used within a ConsultationProvider');
  }
  return context;
};

